import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../api";
import { useI18n } from "../i18n";
import { useCart } from "../cart";

export default function OrderConfirmation() {
  const { id } = useParams();
  const { lang } = useI18n();
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/orders/${id}`);
        if (!cancelled) {
          setOrder(data);
          setLoading(false);
          if (data.status === "paid") clear();
          // Keep polling until status moves out of "pending"
          if (data.status === "pending") setTimeout(poll, 3000);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [id]);

  if (loading) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="text-bone tracking-[0.3em] uppercase text-xs">
          {lang === "fr" ? "Vérification du paiement…" : "Verifying payment…"}
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <div className="font-gothic text-blood text-6xl mb-6">✟</div>
          <p className="text-bone mb-8">{lang === "fr" ? "Commande introuvable." : "Order not found."}</p>
          <Link to="/shop" className="text-xs font-bold tracking-[0.2em] uppercase link-hover">
            {lang === "fr" ? "Retour à la boutique" : "Back to shop"}
          </Link>
        </div>
      </div>
    );
  }

  const isPaid = order.status === "paid";
  const isFailed = order.status === "failed";

  return (
    <div className="bg-black text-white pt-32 pb-32 min-h-screen">
      <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="font-gothic text-blood text-7xl md:text-9xl leading-none mb-8">✟</div>

          {isPaid && (
            <>
              <h1 className="font-black text-4xl md:text-6xl uppercase tracking-tight mb-6">
                {lang === "fr" ? "Le trône est à vous." : "The throne is yours."}
              </h1>
              <p className="text-white/70 leading-relaxed mb-10">
                {lang === "fr"
                  ? "Paiement reçu. Votre commande Beaudoin est entrée dans notre atelier de Saint-Barthélemy. Vous recevrez un email de confirmation et le tracking d'expédition sous 48h."
                  : "Payment received. Your Beaudoin order has entered our Saint-Barthélemy atelier. You will receive a confirmation email and shipping tracking within 48 hours."}
              </p>
            </>
          )}

          {isFailed && (
            <>
              <h1 className="font-black text-4xl md:text-6xl uppercase tracking-tight mb-6 text-blood">
                {lang === "fr" ? "Paiement échoué" : "Payment failed"}
              </h1>
              <p className="text-white/70 leading-relaxed mb-10">
                {lang === "fr" ? "Le paiement n'a pas abouti. Aucun montant n'a été prélevé." : "The payment did not complete. No amount has been charged."}
              </p>
            </>
          )}

          {!isPaid && !isFailed && (
            <>
              <h1 className="font-black text-4xl md:text-6xl uppercase tracking-tight mb-6">
                {lang === "fr" ? "Paiement en attente" : "Payment pending"}
              </h1>
              <p className="text-white/70 leading-relaxed mb-10">
                {lang === "fr"
                  ? "Votre paiement est en cours de validation. Cette page se mettra à jour automatiquement."
                  : "Your payment is being validated. This page will refresh automatically."}
              </p>
            </>
          )}

          <div className="border border-white/10 p-6 mb-10 text-left">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-4">
              {lang === "fr" ? "Détails de commande" : "Order details"}
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/60">{lang === "fr" ? "Référence" : "Order ID"}</span>
                <span className="font-mono text-xs">{order.id.slice(0, 8).toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">{lang === "fr" ? "Statut" : "Status"}</span>
                <span className={isPaid ? "text-green-400" : isFailed ? "text-blood" : "text-bone"}>
                  {order.status.toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Total</span>
                <span className="font-bold">€{order.amount.toFixed(2)}</span>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-white/10 space-y-2">
              {(order.items || []).map((it, i) => (
                <div key={`item-${i}`} className="flex justify-between text-sm">
                  <span>{it.name}{it.size ? ` — ${it.size}` : ""} ×{it.quantity}</span>
                  <span className="text-bone">€{(it.unit_price * it.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>

          <Link
            to="/shop"
            className="inline-block bg-blood text-white px-10 py-4 text-xs font-bold tracking-[0.25em] uppercase hover:bg-blood-deep transition-colors"
          >
            {lang === "fr" ? "Continuer" : "Continue"}
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
